import { yupResolver } from "@hookform/resolvers/yup"
import { format, parse } from "date-fns"
import PropTypes from "prop-types"
import React, { useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation } from "react-query"

import Button from "src/components/Button"
import { ADD_NEW_BOAT } from "src/components/ContactSearch/constants"
import Form from "src/components/Form"

import { createShortTermWaitlistEntry } from "src/api/Waitlist/shortTerm"

import {
  ContactInput,
  FirstDayInput,
  LastDayInput,
  SpaceTypeSelector,
  SpecialRequestInput,
} from "./inputs"
import { schema } from "./schema"

const ShortTermWaitlistForm = ({
  marinaSlug,
  firstDay,
  lastDay,
  spaceType,
  multiDockage,
}) => {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      first_day: firstDay ? parse(firstDay, "MM/dd/yyyy", new Date()) : null,
      last_day: lastDay ? parse(lastDay, "MM/dd/yyyy", new Date()) : null,
      contact: null,
      contact_id: null,
      contact_boat: null,
      contact_boat_id: null,
      special_request: "",
      space_type: spaceType,
    },
  })
  const [contactId, contactBoatId] = methods.watch([
    "contact_id",
    "contact_boat_id",
  ])
  const addingNewBoat = contactBoatId === ADD_NEW_BOAT

  const {
    mutate: createWaitlist,
    isLoading: loadingCreateWaitlist,
    isSuccess: successCreatingWaitlist,
    isError: errorCreatingWaitlist,
    reset: resetCreateWaitlistMutation,
  } = useMutation(
    (data) => createShortTermWaitlistEntry({ marinaSlug, data }),
    {
      onSuccess: (response) => {
        // send the user to the waitlist show page
        window.location.href = response.redirect_to
      },
    }
  )

  const onSubmit = (formData) => {
    const params = {
      first_day: format(formData.first_day, "MM/dd/yyyy"),
      last_day: format(formData.last_day, "MM/dd/yyyy"),
      special_request: formData.special_request.trim(),
      space_type: formData.space_type,
      contact_id: formData.contact_id,
      contact_boat_id: formData.contact_boat_id,
    }
    createWaitlist(params)
  }

  useEffect(() => {
    /*
    If there is an error creating a waitlist, we show an error message.
    This useEffect will reset the state of the createWaitlist mutation if any fields are changed.
    This means that if a waitlist creation error message is present, it will be cleared after making changes to the form.
     */
    const subscription = methods.watch(() => {
      if (errorCreatingWaitlist) {
        resetCreateWaitlistMutation()
      }
    })
    return () => subscription.unsubscribe()
  }, [methods.watch, errorCreatingWaitlist])

  return (
    <div data-testid="dockmaster-short-term-waitlist-entry-form">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <ContactInput />
          <hr />
          <div className="flex flex-col space-y-4">
            <div className="flex w-full flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0">
              <FirstDayInput />
              <LastDayInput />
            </div>
            {multiDockage ? <SpaceTypeSelector /> : null}
            <SpecialRequestInput />
            <Button
              variant="primary"
              type="submit"
              isLoading={loadingCreateWaitlist || successCreatingWaitlist}
              disabled={addingNewBoat || !contactId || !contactBoatId}
            >
              Add to Waitlist
            </Button>
            {errorCreatingWaitlist ? (
              <div className="w-full text-center">
                <Form.Error>
                  There was an error creating the waitlist entry. Please try
                  again later or contact support at mayday@dockwa.com.
                </Form.Error>
              </div>
            ) : null}
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

ShortTermWaitlistForm.propTypes = {
  marinaSlug: PropTypes.string.isRequired,
  firstDay: PropTypes.string,
  lastDay: PropTypes.string,
  spaceType: PropTypes.oneOf(["dock_or_mooring", "dock", "mooring"]),
  multiDockage: PropTypes.bool,
}
export default ShortTermWaitlistForm
